/**
 * Marketing Flex Table — what can be grouped by and what can be measured.
 *
 * Pure data and formatting; no React. `pivot.ts` reads it to build the grid and
 * the page reads it to build its pickers.
 */

export type DimensionKey =
  | 'day'
  | 'week'
  | 'month'
  | 'year'
  | 'sourceCategory'
  | 'source'
  | 'patientType'
  | 'utmSource'
  | 'utmMedium'
  | 'utmCampaign'
  | 'utmTerm'
  | 'utmContent'
  | 'location'
  | 'planType'
  | 'appointmentType';

export interface DimensionDefinition {
  key: DimensionKey;
  label: string;
  /** True for the date dimensions: natural order is by date, not by label. */
  chronological: boolean;
}

export const DIMENSIONS: Record<DimensionKey, DimensionDefinition> = {
  day: { key: 'day', label: 'Day', chronological: true },
  week: { key: 'week', label: 'Week', chronological: true },
  month: { key: 'month', label: 'Month', chronological: true },
  year: { key: 'year', label: 'Year', chronological: true },
  sourceCategory: { key: 'sourceCategory', label: 'Lead Source Category', chronological: false },
  source: { key: 'source', label: 'Lead Source', chronological: false },
  patientType: { key: 'patientType', label: 'Patient Type', chronological: false },
  utmSource: { key: 'utmSource', label: 'UTM Source', chronological: false },
  utmMedium: { key: 'utmMedium', label: 'UTM Medium', chronological: false },
  utmCampaign: { key: 'utmCampaign', label: 'UTM Campaign', chronological: false },
  utmTerm: { key: 'utmTerm', label: 'UTM Term', chronological: false },
  utmContent: { key: 'utmContent', label: 'UTM Content', chronological: false },
  location: { key: 'location', label: 'Location', chronological: false },
  planType: { key: 'planType', label: 'Plan Type', chronological: false },
  appointmentType: { key: 'appointmentType', label: 'Appointment Type', chronological: false },
};

export interface DimensionGroup {
  label: string;
  dimensions: DimensionKey[];
}

export const DIMENSION_GROUPS: DimensionGroup[] = [
  { label: 'Date', dimensions: ['day', 'week', 'month', 'year'] },
  { label: 'Lead source', dimensions: ['sourceCategory', 'source'] },
  { label: 'Patient', dimensions: ['patientType'] },
  { label: 'UTM', dimensions: ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent'] },
  { label: 'Location', dimensions: ['location'] },
  { label: 'Service', dimensions: ['planType', 'appointmentType'] },
];

/** The label every record lacking the chosen dimension groups under (R12). */
export const NOT_SET_LABEL = 'Not set';

/** The additive parts every metric is computed from. Money is integer cents. */
export interface Measures {
  leads: number;
  converted: number;
  booked: number;
  showed: number;
  noShows: number;
  cancelled: number;
  newPatients: number;
  newCustomers: number;
  revenueCents: number;
}

export type BaseMeasure = keyof Measures;

export const BASE_MEASURES: BaseMeasure[] = [
  'leads',
  'converted',
  'booked',
  'showed',
  'noShows',
  'cancelled',
  'newPatients',
  'newCustomers',
  'revenueCents',
];

export function emptyMeasures(): Measures {
  return { leads: 0, converted: 0, booked: 0, showed: 0, noShows: 0, cancelled: 0, newPatients: 0, newCustomers: 0, revenueCents: 0 };
}

export function addMeasures(target: Measures, source: Measures): void {
  for (const key of BASE_MEASURES) target[key] += source[key];
}

export type MetricKey =
  | 'leads'
  | 'converted'
  | 'leadConversion'
  | 'booked'
  | 'showed'
  | 'noShows'
  | 'cancelled'
  | 'showRate'
  | 'newPatients'
  | 'newCustomers'
  | 'customerRate'
  | 'revenue'
  | 'revenuePerNewCustomer';

/** number = integer count; currency = integer cents; percent = a fraction (0.125). */
export type MetricFormat = 'number' | 'currency' | 'percent';

export type MetricCategory = 'Leads' | 'Appointments' | 'Patients' | 'Revenue';

export interface MetricDefinition {
  key: MetricKey;
  label: string;
  format: MetricFormat;
  category: MetricCategory;
  /** Always computed from summed parts, so ratios are never summed or averaged (R20). */
  compute: (m: Measures) => number | null;
  /**
   * Blank on a row that holds a single patient type (a Patient Type row, or
   * any row when the filter selects one type): its parts are typed
   * differently (a new customer is always New, their revenue by R16), so the
   * New and Existing slices would not mean what the label says. A row that
   * holds both types, Totals included, adds both parts up and shows it.
   */
  blankByPatientType?: boolean;
}

/** A ratio whose denominator is zero has no value — never NaN or Infinity. */
function ratio(numerator: number, denominator: number): number | null {
  return denominator === 0 ? null : numerator / denominator;
}

export const METRICS: Record<MetricKey, MetricDefinition> = {
  leads: { key: 'leads', label: 'Leads', format: 'number', category: 'Leads', compute: (m) => m.leads },
  converted: { key: 'converted', label: 'Converted Leads', format: 'number', category: 'Leads', compute: (m) => m.converted },
  leadConversion: {
    key: 'leadConversion',
    label: 'Lead Conversion %',
    format: 'percent',
    category: 'Leads',
    compute: (m) => ratio(m.converted, m.leads),
  },
  booked: { key: 'booked', label: 'Booked Appointments', format: 'number', category: 'Appointments', compute: (m) => m.booked },
  showed: { key: 'showed', label: 'Showed', format: 'number', category: 'Appointments', compute: (m) => m.showed },
  noShows: { key: 'noShows', label: 'No-Shows', format: 'number', category: 'Appointments', compute: (m) => m.noShows },
  cancelled: { key: 'cancelled', label: 'Cancelled', format: 'number', category: 'Appointments', compute: (m) => m.cancelled },
  showRate: {
    key: 'showRate',
    label: 'Show Rate',
    format: 'percent',
    category: 'Appointments',
    compute: (m) => ratio(m.showed, m.showed + m.noShows),
  },
  newPatients: { key: 'newPatients', label: 'New Patients', format: 'number', category: 'Patients', compute: (m) => m.newPatients },
  newCustomers: { key: 'newCustomers', label: 'New Customers', format: 'number', category: 'Patients', compute: (m) => m.newCustomers },
  customerRate: {
    key: 'customerRate',
    label: 'Customer Rate %',
    format: 'percent',
    category: 'Patients',
    compute: (m) => ratio(m.newCustomers, m.newPatients),
  },
  revenue: { key: 'revenue', label: 'Revenue', format: 'currency', category: 'Revenue', compute: (m) => m.revenueCents },
  revenuePerNewCustomer: {
    key: 'revenuePerNewCustomer',
    label: 'Revenue per New Customer',
    format: 'currency',
    category: 'Revenue',
    compute: (m) => ratio(m.revenueCents, m.newCustomers),
    blankByPatientType: true,
  },
};

/** Default order, and every metric is selected by default (R14). */
export const DEFAULT_METRICS: MetricKey[] = [
  'leads',
  'converted',
  'leadConversion',
  'booked',
  'showed',
  'noShows',
  'cancelled',
  'showRate',
  'newPatients',
  'newCustomers',
  'customerRate',
  'revenue',
  'revenuePerNewCustomer',
];

export interface MetricGroup {
  label: MetricCategory;
  metrics: MetricKey[];
}

export const METRIC_GROUPS: MetricGroup[] = (['Leads', 'Appointments', 'Patients', 'Revenue'] as MetricCategory[]).map(
  (label) => ({ label, metrics: DEFAULT_METRICS.filter((key) => METRICS[key].category === label) }),
);

const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const INTEGER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const PERCENT = new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Display formatting: currency from cents, percent with one decimal, null as empty. */
export function formatMetric(value: number | null, format: MetricFormat): string {
  if (value === null || !Number.isFinite(value)) return '';
  if (format === 'currency') return CURRENCY.format(value / 100);
  if (format === 'percent') return PERCENT.format(value);
  return INTEGER.format(value);
}
